<template>
  <div>
    <!-- Panel 1: 三维变量模型 -->
    <div class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot cyan"></span>三维变量模型<span class="pid">结构分析基础</span>
      </div>
      <div class="panel-body">
        <p class="lead">渠道结构的三个核心变量：<strong>长度</strong>（中间层级）、<strong>宽度</strong>（每层密度）、<strong>广度</strong>（类型多样性）。</p>
        <div class="g3">
          <div class="dcell">
            <h4>长度 · 线上触点层级</h4>
            <li>零级：DTC官网/小程序/App → 用户</li>
            <li>一级：品牌 → 平台旗舰店 → 用户</li>
            <li>二级：品牌 → 经销商 → 平台店铺 → 用户</li>
            <li>多级：品牌 → 分销商 → 团长/达人 → 用户</li>
          </div>
          <div class="dcell">
            <h4>宽度 · 各层级密度</h4>
            <li>独家宽度：每平台仅1家店铺</li>
            <li>选择宽度：每平台2-3家授权店铺</li>
            <li>密集宽度：每平台多家店铺+分销体系</li>
            <li>过度密集 → 内部竞争、价格混乱</li>
          </div>
          <div class="dcell">
            <h4>广度 · 线上业态多样性</h4>
            <li>货架电商：天猫、京东、拼多多</li>
            <li>内容电商：抖音、快手、小红书</li>
            <li>社交电商：微信小程序、社群团购</li>
            <li>即时零售：美团、京东到家</li>
          </div>
        </div>
      </div>
    </div>

    <!-- Panel 2: 生命周期 -->
    <div class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot green"></span>渠道结构生命周期<span class="pid">动态演化分析</span>
      </div>
      <div class="panel-body">
        <p class="lead">渠道结构并非一成不变，需随产品/市场/平台生命周期动态调整。</p>
        <div class="glc">
          <div class="lc i1"><strong>导入期</strong>少量渠道试水<br>验证 PMF</div>
          <div class="lc i2"><strong>成长期</strong>快速铺渠道<br>抢占份额</div>
          <div class="lc i3"><strong>成熟期</strong>结构稳定<br>精细化调整</div>
          <div class="lc i4"><strong>衰退期</strong>收缩瘦身<br>保留核心</div>
        </div>
        <div class="lc-desc">
          <div><strong>导入期：</strong>选择1-2个核心平台验证匹配度，结构简单、层级少。</div>
          <div><strong>成长期：</strong>快速拓展平台和业态，需警惕无序扩张。</div>
          <div><strong>成熟期：</strong>结构趋于稳定，重点转向优化（剔除低效渠道、增强DTC占比）。</div>
          <div><strong>衰退期：</strong>主动缩减渠道，保留高价值核心渠道。</div>
        </div>
      </div>
    </div>

    <!-- Panel 3: 八要素 -->
    <div class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot cyan"></span>渠道设计八要素<span class="pid">适配性诊断基础</span>
      </div>
      <div class="panel-body">
        <p class="lead">设计或调整渠道结构时必须权衡的八个维度。</p>
        <div class="g4">
          <div v-for="el in eightElements" :key="el.num" class="e8">
            <div class="en">{{ el.num }}</div>
            <div class="et">{{ el.title }}</div>
            <div class="ed">{{ el.desc }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Panel 4: 扁平化 -->
    <div class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot cyan"></span>渠道扁平化<span class="pid">趋势与分析</span>
      </div>
      <div class="panel-body">
        <p class="lead">减少中间层级、缩短用户触达路径是全渠道管理的必然趋势。</p>
        <div class="g2">
          <div style="font-size:0.83rem;color:var(--text2);">
            <strong style="color:var(--text);">传统结构</strong><br>
            品牌 → 总代 → 省代 → 市代 → 零售商 → 消费者<br>
            <span style="color:var(--text3);font-size:0.7rem;">6级传导，层层衰减</span><br><br>
            <strong style="color:var(--text);">扁平化结构</strong><br>
            品牌 → DTC官网/平台旗舰店 → 消费者<br>
            <span style="color:var(--text3);font-size:0.7rem;">1-2级直达，数据闭环</span>
          </div>
          <div style="font-size:0.82rem;">
            <div class="fl"><span class="ck">✓</span>自有触点：官网、小程序、App、企业微信</div>
            <div class="fl"><span class="ck">✓</span>平台直营：天猫/京东旗舰店、抖音品牌号</div>
            <div class="fl"><span class="ck">✓</span>内容直连：小红书品牌号、视频号直播</div>
            <div class="fl"><span class="ck">✓</span>私域沉淀：会员体系、社群运营</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TheoryTab',
  data() {
    return {
      eightElements: [
        { num: '01', title: '产品', desc: '品类特性决定渠道选型' },
        { num: '02', title: '市场', desc: '市场规模与区域分布' },
        { num: '03', title: '企业', desc: '自身资源与团队能力' },
        { num: '04', title: '中间商', desc: '平台/代运营能力匹配' },
        { num: '05', title: '竞争', desc: '竞品渠道结构参照' },
        { num: '06', title: '环境', desc: '平台政策与监管' },
        { num: '07', title: '顾客', desc: '用户购物习惯与触点' },
        { num: '08', title: '成本', desc: '各结构方案经济性' }
      ]
    }
  }
}
</script>
